import { useEffect, useState } from 'react';
import { Card, Button, Spinner, FormField, Badge } from '../components/ui';

const STATUS_OPTIONS = [
  { value:'due',     label:'Due',     color:'#d97706' },
  { value:'paid',    label:'Paid',    color:'#16a34a' },
  { value:'late',    label:'Late',    color:'#dc2626' },
  { value:'partial', label:'Partial', color:'#7c3aed' },
];

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
}

function statusBadge(status) {
  const s = STATUS_OPTIONS.find(o => o.value === status) || STATUS_OPTIONS[0];
  const statusMap = { due:'due_soon', paid:'valid', late:'expired', partial:'due_soon' };
  return <Badge status={statusMap[status] || 'due_soon'} label={s.label} />;
}

const EMPTY_FORM = { tenant_id:'', due_date:'', paid_date:'', amount_gbp:'', status:'due', notes:'' };

export default function RentTracker() {
  const [payments, setPayments]   = useState([]);
  const [properties, setProperties] = useState([]);
  const [tenants, setTenants]     = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editID, setEditID]       = useState(null);
  const [form, setForm]           = useState(EMPTY_FORM);
  const [saving, setSaving]       = useState(false);
  const [selectedProp, setSelectedProp] = useState('');
  const [filterProp, setFilterProp] = useState('');
  const [filterStatus, setFilterStatus] = useState('');

  const h = () => { const ac = localStorage.getItem('lc_access_code'); return { 'Content-Type':'application/json', Authorization:`Bearer ${localStorage.getItem('token')}`, ...(ac ? {'X-Access-Code': ac} : {}) }; };

  useEffect(() => {
    Promise.all([
      fetch('/api/rent-payments', { headers: h() }).then(r => r.json()),
      fetch('/api/properties',    { headers: h() }).then(r => r.json()),
    ]).then(([p, props]) => {
      setPayments(Array.isArray(p) ? p : []);
      setProperties(Array.isArray(props) ? props : []);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    if (!selectedProp) { setTenants([]); return; }
    fetch(`/api/properties/${selectedProp}/tenants`, { headers: h() })
      .then(r => r.json()).then(t => setTenants(Array.isArray(t) ? t : []));
  }, [selectedProp]);

  function openNew() {
    setForm({ ...EMPTY_FORM, due_date: new Date().toISOString().split('T')[0] });
    setSelectedProp('');
    setEditID(null);
    setShowForm(true);
  }

  function openEdit(p) {
    setForm({
      tenant_id: p.tenant_id, due_date: p.due_date, paid_date: p.paid_date || '',
      amount_gbp: String(p.amount_gbp / 100), status: p.status, notes: p.notes || '',
    });
    setSelectedProp(p.property_id);
    setEditID(p.id);
    setShowForm(true);
  }

  async function handleSave() {
    if (!selectedProp) return;
    setSaving(true);
    try {
      const body = {
        tenant_id: form.tenant_id,
        due_date: form.due_date,
        paid_date: form.paid_date || null,
        amount_gbp: Math.round(parseFloat(form.amount_gbp || 0) * 100),
        status: form.status,
        notes: form.notes || null,
      };
      if (editID) {
        await fetch(`/api/rent-payments/${editID}`, { method:'PUT', headers: h(), body: JSON.stringify(body) });
      } else {
        await fetch(`/api/properties/${selectedProp}/rent-payments`, { method:'POST', headers: h(), body: JSON.stringify(body) });
      }
      const p = await fetch('/api/rent-payments', { headers: h() }).then(r => r.json());
      setPayments(Array.isArray(p) ? p : []);
      setShowForm(false);
    } finally { setSaving(false); }
  }

  async function quickMarkPaid(payment) {
    const today = new Date().toISOString().split('T')[0];
    await fetch(`/api/rent-payments/${payment.id}`, {
      method:'PUT', headers: h(),
      body: JSON.stringify({ ...payment, paid_date: today, status:'paid', amount_gbp: payment.amount_gbp }),
    });
    setPayments(ps => ps.map(p => p.id === payment.id ? { ...p, status:'paid', paid_date: today } : p));
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this payment record?')) return;
    await fetch(`/api/rent-payments/${id}`, { method:'DELETE', headers: h() });
    setPayments(ps => ps.filter(p => p.id !== id));
  }

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  let filtered = payments;
  if (filterProp)   filtered = filtered.filter(p => p.property_id === filterProp);
  if (filterStatus) filtered = filtered.filter(p => p.status === filterStatus);

  // Summary stats
  const totalDue  = payments.filter(p => p.status === 'due' || p.status === 'late').reduce((s, p) => s + p.amount_gbp, 0);
  const totalPaid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount_gbp, 0);
  const lateCount = payments.filter(p => p.status === 'late').length;

  if (loading) return <Spinner />;

  return (
    <div className="fade-up">
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12, marginBottom:32, flexWrap:'wrap', gap:16 }}>
        <div>
          <h1 style={{ fontSize:32, fontWeight:300 }}>Rent <em>Tracker</em></h1>
          <p style={{ color:'var(--ink-3)', marginTop:4 }}>Track rent payments and arrears across all properties</p>
        </div>
        <Button onClick={openNew}>+ Log payment</Button>
      </div>

      {/* Summary */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(160px, 1fr))', gap:16, marginBottom:24 }}>
        {[
          { label:'Outstanding', value:`£${(totalDue/100).toFixed(0)}`, color: totalDue > 0 ? '#dc2626' : 'var(--ink)' },
          { label:'Collected',   value:`£${(totalPaid/100).toFixed(0)}`, color:'#16a34a' },
          { label:'Late payments', value:lateCount, color: lateCount > 0 ? '#dc2626' : 'var(--ink)' },
        ].map(s => (
          <Card key={s.label} style={{ textAlign:'center', padding:'20px 16px' }}>
            <p style={{ fontSize:26, fontWeight:700, color:s.color }}>{s.value}</p>
            <p style={{ fontSize:12, color:'var(--ink-3)', marginTop:4 }}>{s.label}</p>
          </Card>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {properties.length > 0 && (
          <select value={filterProp} onChange={e => setFilterProp(e.target.value)}
            style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14 }}>
            <option value="">All properties</option>
            {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
          </select>
        )}
        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
          style={{ padding:'8px 12px', borderRadius:8, border:'1.5px solid var(--border)', background:'var(--bg)', fontSize:14 }}>
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
      </div>

      {/* Payments list */}
      {filtered.length === 0 ? (
        <Card style={{ textAlign:'center', padding:48 }}>
          <p style={{ fontSize:32, marginBottom:12 }}>💷</p>
          <p style={{ fontWeight:600, marginBottom:8 }}>No rent payments recorded</p>
          <p style={{ color:'var(--ink-3)', fontSize:14, marginBottom:20 }}>Track rent due dates and payment history for each tenant.</p>
          <Button onClick={openNew}>+ Log first payment</Button>
        </Card>
      ) : (
        <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
          {filtered.map(p => (
            <Card key={p.id} style={{ padding:'14px 18px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
                <div style={{ flex:1 }}>
                  <div style={{ display:'flex', alignItems:'center', gap:10, flexWrap:'wrap', marginBottom:4 }}>
                    <p style={{ fontWeight:600, fontSize:15 }}>{p.tenant_name}</p>
                    {statusBadge(p.status)}
                  </div>
                  <div style={{ display:'flex', gap:16, flexWrap:'wrap' }}>
                    <span style={{ fontSize:13, color:'var(--ink-3)' }}>🏘 {p.property_address}</span>
                    <span style={{ fontSize:13, color:'var(--ink-3)' }}>📅 Due {formatDate(p.due_date)}</span>
                    {p.paid_date && <span style={{ fontSize:13, color:'var(--ink-3)' }}>✅ Paid {formatDate(p.paid_date)}</span>}
                    <span style={{ fontSize:15, fontWeight:700 }}>£{(p.amount_gbp/100).toFixed(2)}</span>
                  </div>
                  {p.notes && <p style={{ fontSize:13, color:'var(--ink-3)', marginTop:4, fontStyle:'italic' }}>{p.notes}</p>}
                </div>
                <div style={{ display:'flex', gap:8 }}>
                  {p.status !== 'paid' && (
                    <Button size="sm" onClick={() => quickMarkPaid(p)}>Mark paid</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => openEdit(p)}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id)} style={{ color:'var(--danger)' }}>Delete</Button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.6)', zIndex:9999, backdropFilter:'blur(2px)', display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'40px 20px', overflowY:'auto' }}
          onClick={e => e.target === e.currentTarget && setShowForm(false)}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:480, boxShadow:'0 20px 60px rgba(0,0,0,.3)' }}>
            <h3 style={{ fontSize:20, marginBottom:20 }}>{editID ? 'Edit payment' : 'Log rent payment'}</h3>

            <FormField label="Property *">
              <select value={selectedProp} onChange={e => { setSelectedProp(e.target.value); setForm(f => ({ ...f, tenant_id:'' })); }} disabled={!!editID}>
                <option value="">Select property…</option>
                {properties.map(p => <option key={p.id} value={p.id}>{p.address_line1}</option>)}
              </select>
            </FormField>

            {selectedProp && tenants.length > 0 && (
              <FormField label="Tenant *">
                <select value={form.tenant_id} onChange={set('tenant_id')}>
                  <option value="">Select tenant…</option>
                  {tenants.map(t => <option key={t.id} value={t.id}>{t.full_name}</option>)}
                </select>
              </FormField>
            )}

            <FormField label="Amount (£) *">
              <input type="number" step="0.01" min="0" value={form.amount_gbp} onChange={set('amount_gbp')} placeholder="1200.00" />
            </FormField>

            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(280px, 1fr))', gap:12 }}>
              <FormField label="Due date *">
                <input type="date" value={form.due_date} onChange={set('due_date')} />
              </FormField>
              <FormField label="Status">
                <select value={form.status} onChange={set('status')}>
                  {STATUS_OPTIONS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                </select>
              </FormField>
            </div>

            <FormField label="Date paid">
              <input type="date" value={form.paid_date} onChange={set('paid_date')} />
            </FormField>

            <FormField label="Notes">
              <textarea value={form.notes} onChange={set('notes')} rows={2} placeholder="Any notes…" />
            </FormField>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', marginTop:8 }}>
              <Button variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
              <Button loading={saving} onClick={handleSave} disabled={!selectedProp || !form.due_date || !form.amount_gbp}>
                {editID ? 'Save changes' : 'Log payment'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
